import { setLocalAuthKey } from '@/common/auth';
import { CompactButton } from '@/components/buttons';
import { StandardButton } from '@/components/buttons/standard';
import { ContentPanel } from '@/components/content-panel';
import { PrimaryLayout } from '@/layouts/primary';
import { Flex, Title } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { FormikProps } from 'formik';
import { useCallback, useMemo, useRef } from 'react';
import { ChangePasswordForm, ChangePasswordFormValues } from './form';
import { useChangePasswordMutation } from './graphql.generated';

export const ChangePasswordPage = () => {
	const navigate = useNavigate();
	const [changePassword, { error }] = useChangePasswordMutation();
	const ref = useRef<FormikProps<ChangePasswordFormValues>>(null);
	const signupButton = useMemo(
		() => (
			<StandardButton onClick={() => ref.current?.submitForm()}>Change Password</StandardButton>
		),
		[]
	);

	const homeButton = useMemo(
		() => <StandardButton onClick={() => navigate({ to: '/home' })}>Home</StandardButton>,
		[navigate]
	);

	const closeButton = useMemo(
		() => (
			<CompactButton leftSection={<IconX />} onClick={() => navigate({ to: '/account' })}>
				Close
			</CompactButton>
		),
		[navigate]
	);

	const handleSubmit = useCallback(
		async (values: ChangePasswordFormValues) => {
			if (values.password !== values.reenterPassword) {
				ref.current?.setFieldError('reenterPassword', 'Passwords do not match');
				return;
			}

			try {
				const { data } = await changePassword({
					variables: {
						newPassword: values.password,
					},
				});

				if (data?.changePassword?.authToken) {
					setLocalAuthKey(data.changePassword.authToken);
					navigate({ to: '/account' });
				}
			} catch (error) {
				console.error(error);
			}
		},
		[changePassword, navigate]
	);

	return (
		<PrimaryLayout
			header={{ title: 'Change Password', backTo: '/account', children: closeButton }}
			footer={{ children: [error ? homeButton : signupButton] }}
		>
			<ContentPanel>
				<Flex direction="column" h="100%" w="100%" flex={1} justify="center">
					{error ? (
						<Title order={2} ta="center" mt="xl">
							An error occurred, please try again later or contact{' '}
							<a
								href="mailto:support@curbcycle.com.au?subject=Technical issue on Change Password page of Curby App"
								target="_blank"
								rel="noopener"
							>
								support@curbcycle.com.au
							</a>
						</Title>
					) : (
						<ChangePasswordForm onSubmit={handleSubmit} formRef={ref} />
					)}
				</Flex>
			</ContentPanel>
		</PrimaryLayout>
	);
};
